'use client';

/**
 * Resumes a checkout that was interrupted by sign-up.
 *
 * Flow: a signed-out visitor clicks a plan → we send them to /sign-up with
 * `redirect_url=/pricing?plan=…&interval=…&checkout=1` → Clerk returns them
 * here → this fires the checkout they already chose.
 *
 * Without it, returning users land back on the pricing page and have to pick the
 * same plan a second time. That second decision is where the funnel died: the
 * old flow bounced them through /pricing → /dashboard/pricing and discarded the
 * plan param on arrival, so nobody ever completed the round trip.
 */

import { Suspense, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { useSignedInHint } from '@/lib/auth/use-signed-in-hint';
import { startCheckout } from '@/lib/billing/start-checkout';
import { claimCheckoutResume, checkoutResumeFinishedUrl } from '@/lib/billing/checkout-resume';

/** Self-wrapped: useSearchParams needs a Suspense boundary to keep host pages static. */
export function AutoCheckout() {
  return (
    <Suspense fallback={null}>
      <AutoCheckoutInner />
    </Suspense>
  );
}

function AutoCheckoutInner() {
  const isSignedIn = useSignedInHint();
  const searchParams = useSearchParams();
  const router = useRouter();
  const firedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const shouldCheckout = searchParams.get('checkout') === '1';
  useEffect(() => {
    const args = claimCheckoutResume(Boolean(isSignedIn), searchParams, firedRef);
    if (!args) return;

    setBusy(true);
    void startCheckout(args).then((result) => {
      if (result.status === 'redirecting') return; // navigating away
      setBusy(false);

      if (result.status === 'error') setError(result.message);
      // 'already_subscribed' needs no banner here — the plan cards below already
      // mark the current plan, so a duplicate message would just be noise.

      // Drop ?checkout=1 so a refresh or back-navigation doesn't retry.
      router.replace(checkoutResumeFinishedUrl(new URLSearchParams(searchParams.toString())), {
        scroll: false,
      });
    });
  }, [isSignedIn, searchParams, router]);

  if (!shouldCheckout) return null;

  if (busy) {
    return (
      <div className="mb-8 rounded-lg border border-[rgb(var(--primary))]/30 bg-[rgb(var(--primary))]/10 p-4 text-center">
        <p className="text-sm text-[rgb(var(--foreground))]">Taking you to checkout…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="mb-8 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-center"
      >
        <p className="text-sm text-[rgb(var(--foreground))]">
          {error} Pick your plan below to try again.
        </p>
      </div>
    );
  }

  return null;
}
